// Latency SLO card on the supervisor wallboard (architecture §5.1): p50 / p95 / p99 per pipeline segment from
// GET /api/slo, computed in the database from the per-hop alarm timestamps, so it works without Grafana. Refreshed
// every 30 s (percentiles over an hour don't move faster than that, and the wallboard itself polls every 2 s).

import { useEffect, useState } from 'react';
import { api, type SloReport, type SloSegment } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { InlineError } from '../components/ui.tsx';

const WINDOWS = ['15m', '1h', '24h', '7d'] as const;
const POLL_MS = 30_000;

function fmtS(v: number | null): string {
  if (v === null) return '—';
  if (v < 1) return `${Math.round(v * 1000)} ms`;
  if (v < 60) return `${v.toFixed(v < 10 ? 2 : 1)} s`;
  return `${Math.floor(v / 60)} min ${Math.round(v % 60)} s`;
}

function StatusPill({ s }: { s: SloSegment }) {
  if (s.status === 'no_data') return <span className="pill tiny">no data</span>;
  if (s.status === 'info') return <span className="pill tiny info">info</span>;
  return s.status === 'ok' ? <span className="pill tiny ok">met</span> : <span className="pill tiny bad">over</span>;
}

export function SloCard() {
  const [win, setWin] = useState<(typeof WINDOWS)[number]>('1h');
  const [data, setData] = useState<SloReport | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const ctl = new AbortController();
    const tick = async () => {
      try {
        const r = await api.slo(win, ctl.signal);
        if (stopped) return;
        setData(r);
        setError(null);
      } catch (e) {
        if (!stopped && !ctl.signal.aborted) setError(e);
      }
      if (!stopped) timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => {
      stopped = true;
      ctl.abort();
      if (timer) clearTimeout(timer);
    };
  }, [win]);

  const breaches = data?.segments.filter((s) => s.status === 'breach').length ?? 0;
  return (
    <div className="card2">
      <div className="card-h">
        <h3>
          <Icon name="chart" />
          Latency SLO
          {breaches > 0 && <span className="pill bad tiny">{breaches} over target</span>}
        </h3>
        <span className="card-r">
          <span className="seg" role="radiogroup" aria-label="SLO window">
            {WINDOWS.map((w) => (
              <button key={w} type="button" role="radio" aria-checked={win === w} className={win === w ? 'on' : ''} onClick={() => setWin(w)}>
                {w}
              </button>
            ))}
          </span>
        </span>
      </div>
      {error ? <InlineError error={error} /> : null}
      {data && (
        <>
          <div className="note">
            {data.alarms} alarm{data.alarms === 1 ? '' : 's'} in the last {data.window} ({data.perMinute}/min). Targets are p95 (architecture §5.1).
          </div>
          <div className="tw">
            <table className="wb-slo">
              <thead>
                <tr>
                  <th scope="col">Segment</th>
                  <th scope="col">n</th>
                  <th scope="col">p50</th>
                  <th scope="col">p95</th>
                  <th scope="col">p99</th>
                  <th scope="col">Target</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {data.segments.map((s) => (
                  <tr key={s.key} title={s.note ?? undefined}>
                    <td>
                      {s.label}
                      {s.targetS !== null && s.overTarget > 0 && <div className="note">{s.overTarget} over target</div>}
                    </td>
                    <td className="mono">{s.count}</td>
                    <td className="mono">{fmtS(s.p50)}</td>
                    <td className={`mono${s.status === 'breach' ? ' late' : ''}`}>{fmtS(s.p95)}</td>
                    <td className="mono">{fmtS(s.p99)}</td>
                    <td className="mono">{s.targetS === null ? "—" : `< ${s.targetS} s`}</td>
                    <td>
                      <StatusPill s={s} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
